// Dashboard pure-function helpers — extracted from Dashboard.js
// (Feb 2026 refactor step 3). These are stateless utilities that do
// not depend on any React context — safe to import from any component.
import React from 'react';
import { Play, FileText, Image as ImageIcon, Clock, Loader2, AlertTriangle, Folder } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { CATEGORY_ICONS } from './constants';

// Detects RTL Hebrew text — used to flip dir="rtl" on titles/notes
// that were saved in Hebrew, so line-wrapping and punctuation render
// correctly. Threshold: more Hebrew chars than Latin chars.
export const isHebrew = (text) => {
    if (!text) return false;
    const hebrewChars = (text.match(/[\u0590-\u05FF]/g) || []).length;
    const latinChars = (text.match(/[a-zA-Z]/g) || []).length;
    return hebrewChars > latinChars;
};

// Short "Feb 3" style date pill for the card footer.
export const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Small lucide icon that matches the content_type of a bookmark.
export const getContentIcon = (contentType) => {
    switch (contentType) {
        case 'video': return <Play className="w-4 h-4" />;
        case 'image': return <ImageIcon className="w-4 h-4" />;
        default: return <FileText className="w-4 h-4" />;
    }
};

// Whether the bookmark should show a "Needs Review" quality banner.
// Suppressed when we have a summary (successful extraction) or the
// bookmark is a place (Google Maps can't be scraped — expected).
export const hasQualityIssues = (bookmark) => {
    if (bookmark.summary) return false;
    if (bookmark.content_subtype === 'place') return false;
    return bookmark.quality_issues && bookmark.quality_issues.length > 0;
};

// Human-readable message for the first known quality issue on the
// bookmark, or a generic fallback. Order-sensitive: most-specific
// issues win over generic "content may be incomplete".
export const getQualityMessage = (issues) => {
    if (!issues || issues.length === 0) return null;
    if (issues.includes('login_required')) return 'This link requires login (Instagram/Facebook). Paste the text manually to generate a summary.';
    if (issues.includes('incomplete_text')) return 'Full text could not be imported. Tap to paste the original text.';
    if (issues.includes('login_page_content')) return 'Content may be a login page - try reprocessing';
    if (issues.includes('no_transcript') && issues.includes('generic_title')) return 'Could not extract video content - try reprocessing';
    if (issues.includes('no_transcript')) return 'Video transcript unavailable - try reprocessing';
    if (issues.includes('no_summary')) return 'Summary not generated - try reprocessing';
    if (issues.includes('generic_title')) return 'Title looks generic - content may not have been extracted properly';
    return 'Content may be incomplete - try reprocessing';
};

// Small status pill for non-happy-path bookmark states. Returns null
// for completed bookmarks with no quality issues (i.e. we don't
// pollute the card with a redundant "Done" badge).
export const getStatusBadge = (status, bookmark = null) => {
    if (status === 'completed' && bookmark && hasQualityIssues(bookmark)) {
        return (
            <Badge variant="secondary" className="font-mono text-xs bg-amber-100 text-amber-700">
                <AlertTriangle className="w-3 h-3 mr-1" /> Needs Review
            </Badge>
        );
    }
    const stageLabels = { extracting: 'Reviewing', downloading: 'Reviewing', transcribing: 'Transcribing', analyzing: 'Analyzing', enriching: 'Finishing' };
    const stage = bookmark?.processing_stage;
    switch (status) {
        case 'pending':
            return <Badge variant="secondary" className="font-mono text-xs"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
        case 'processing':
            return <Badge variant="secondary" className="font-mono text-xs bg-accent text-accent-foreground"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {stage ? stageLabels[stage] || 'Processing' : 'Processing'}</Badge>;
        case 'failed':
            return <Badge variant="destructive" className="font-mono text-xs">Failed</Badge>;
        case 'completed':
        default:
            return null;
    }
};

// Resolves an icon-name string (from a category doc) to a lucide
// component, defaulting to Folder for unknown/missing icons.
export const getCategoryIcon = (iconName) => CATEGORY_ICONS[iconName] || Folder;

// Heuristic: does this search query read like a natural-language
// question rather than a keyword lookup? Used by the "Try AI search"
// escalation chip to decide when to nudge the user toward the LLM
// pipeline even if regex returned decent results.
//
// True when ANY of the following holds:
//   - ends with `?`
//   - starts with an English wh-/aux- interrogative (who, what, how,
//     why, when, where, which, can, does, is, are, should)
//   - 5+ words (keyword lookups are almost always 1-4 words)
//
// Kept intentionally simple. False positives (e.g. "what to cook for
// dinner") are harmless — they only show the escalation chip; the
// regex results still render underneath. False negatives are also OK
// because the low-results branch will catch most of them.
export const looksLikeNaturalLanguage = (query) => {
    if (!query || typeof query !== 'string') return false;
    const q = query.trim();
    if (!q) return false;
    if (q.endsWith('?')) return true;
    const words = q.split(/\s+/);
    if (words.length >= 5) return true;
    const first = words[0].toLowerCase();
    return /^(who|what|how|why|when|where|which|can|does|do|is|are|should|will|would|could)$/.test(first);
};
